import React from "react";
import { newId } from "./lib/ids";
import { EmptyState, Icon, Portal, StatCard, confirmDialog, clickable, estimateRoom, limitPrompt } from "./registry";
import { ensureLeaflet } from "./lib/leaflet";
import { lensOf, leadInLens, sourceInLens, leadTypesOf, heatFor, estLabelFor, builtYearOf, radarType, RADAR_COPY, signalOptionsFor, sampleLeadsFor, concreteStarter, concreteDraftForm, radarQuoteNote, CONCRETE_JOB_CHOICES } from "./lib/radar.lens";   // the radar under a lens (plan §13 item 20)
import { buildConcreteQuote } from "./lib/engine.concrete";   // a concrete lead's draft estimate
/* ============================================================
   Fence Works — Lead Radar
   ------------------------------------------------------------
   Newly-permitted pool projects in your county, surfaced as claimable leads
   with a draft quote one tap away. A new pool permit means a household just
   committed $60–90k to their backyard and has a hard safety-inspection
   deadline before certificate of completion — and an open decision between
   four §515.27 compliance options, one of which you install.

   COPY GUARDRAIL (do not soften this): Florida §515.27 does NOT require a
   fence. It requires at least ONE of four safety options — barrier, approved
   safety cover, door/window exit alarms, or self-closing/self-latching
   devices. Nothing in this UI may say a fence is "legally required"; sell the
   timing and the spend signal, never a legal mandate.

   Data: window.VFP_RADAR (lib/supabase.ts) → pool_permits / lead_claims from
   supabase_schema_leadradar.sql, ingested weekly by server/src/radar/.
   Claim exclusivity + plan quotas are enforced in the database, not here.

   Under the Concrete lens (lib/radar.lens.ts; plan §13 item 20) the same
   tables serve the concrete service: driveway & flatwork permits, closings
   and settling-age homes, each with its own heat curve, and a draft ESTIMATE
   (lib/engine.concrete.ts) in place of the fence quote. The lens decides
   which lead types, sources and samples this screen shows.
   ============================================================ */
const LR_money0 = window.VFP.money0;
const HOUR = 3600000;

const COUNTY_LABELS: any = { hillsborough: "Hillsborough", pinellas: "Pinellas", pasco: "Pasco", brown: "Brown" };
const countyLabel = (k: string) => COUNTY_LABELS[k] || (k ? k[0].toUpperCase() + k.slice(1) : "");

/* Lead sources — the signal each one carries and the HONEST pitch coaching
   for it. The mandate framing is deliberately per-market: FL §515.27 is four
   options (a fence is never "required"); WI barrier rules are municipal
   ordinances that usually DO mandate a barrier but carry exemptions
   (above-ground walls, lockable spa covers) — verify the municipality. */
const SOURCE_META: any = {
  pool_permit: {
    label: "Pool permit", verb: "filed",
    why: (lead: any) => lead.state === "WI"
      ? <>This household is putting in a pool, and in most Brown County municipalities a <b>barrier is mandated by local ordinance</b> — a real purchase with a deadline, not a want. Mind the exemptions before you pitch "required by code": above-ground walls over the minimum height and lockable spa covers often qualify. Check that municipality's ordinance, then sell the deadline.</>
      : <><b>Why this is hot:</b> this household just committed $60–90k to a pool and must pass a <b>safety inspection before their certificate of completion</b>. Florida §515.27 gives them four ways to pass — barrier, approved safety cover, exit alarms, or self-latching doors — and a fence is the one that doesn't make the backyard feel like an alarm system. <span className="faint">Sell the deadline and the yard — never say a fence is "legally required," because it isn't.</span></>,
  },
  hot_tub_permit: {
    label: "Hot tub permit", verb: "filed",
    why: () => <>A hot tub going in usually means backyard investment mode. Barrier rules vary by municipality and <b>lockable covers commonly satisfy them</b> — so don't lead with code. Lead with privacy: nobody wants to soak in view of the neighbors.</>,
  },
  closing: {
    label: "Recent closing", verb: "closed",
    why: (lead: any) => <><b>Why this is hot:</b> new owners do their fence in the first 6–24 months — the dog, the kids, the privacy from brand-new neighbors, making the place theirs.{lead.signal_amount ? <> They just put {LR_money0(lead.signal_amount)} into this home;</> : null} the fence is the classic year-one project. No mandate angle here — sell the fresh start.</>,
  },
  new_construction: {
    label: "New construction", verb: "built",
    why: () => <><b>Why this is hot:</b> 18 months to 3 years in, the builder's sod has settled, the yard is still bare, and the fence is the next project on the list. The mortgage shock has worn off and the backyard budget is back.</>,
  },
  // the concrete service's signals (plan §13 item 20) — a signal, never a diagnosis
  concrete_permit: {
    label: "Concrete permit", verb: "filed",
    why: () => <><b>Why this is hot:</b> a driveway, sidewalk, apron or slab permit means new concrete going in — and new concrete wants <b>sealing after its 28-day cure</b> (a garage slab, a coating). The slab next to the one they replaced is usually the one that sank: a lift costs a fraction of what they just paid. <span className="faint">Pitch the timing and the neighbour slab — nothing here says their concrete has a problem until you've seen it.</span></>,
  },
  aging_slab: {
    label: "Settling-age home", verb: "built",
    why: (lead: any) => { const y = builtYearOf(lead); return <><b>Why this is hot:</b> a home built {y ? `in ${y}` : "15–30 years ago"} has had that long for the driveway, walk and stoop to settle — the trip edge at the garage apron, the walk that pools water. Nobody has called yet; the first one down the street who does gets the neighbours watching the rig. <span className="faint">An age is a signal, not a diagnosis — offer the walk-through, never a problem you haven't seen.</span></>; },
  },
};
const sourceMeta = (lead: any) => SOURCE_META[lead.source] || SOURCE_META.pool_permit;

/* ---- record search vocabulary -------------------------------------------
   Signals mirror pool_permits.source. "Recently moved" and "recently sold"
   are the SAME public record — a deed transfer / RETR closing — seen from
   the buyer's and seller's side; outreach goes to the new owner (the grantee
   is what's ingested; grantor/seller columns are deliberately dropped).
   Pool + hot tub share ONE chip: they're both backyard-water permits, and
   whether a hot tub pulls its own permit varies by market (WI municipalities
   commonly permit the electrical/plumbing and apply barrier ordinances to
   spas — with lockable-cover exemptions; FL plug-in portables usually skip
   permits, in-ground spas don't). One chip, both types, no empty chip. */
// The chips themselves come from lib/radar.lens.ts signalOptionsFor(lens):
// under the Concrete lens they are closings, driveway & flatwork permits and
// settling-age homes.
/* Date windows cut on the SIGNAL date (filed / closed / built). from/to are
   days-back bounds: permit_date >= today−from and < today−to. The aged
   buckets are the point — "moved 6–9 months ago" is prime fence outreach
   (new owners fence in the first 6–24 months), and search reaches records
   the curated feed has already aged out. */
const SEARCH_WINDOWS = [
  { id: "30d", label: "Last 30 days", from: 30, to: null },
  { id: "60d", label: "Last 60 days", from: 60, to: null },
  { id: "90d", label: "Last 90 days", from: 90, to: null },
  { id: "3-6m", label: "3–6 mo ago", from: 182, to: 91 },
  { id: "6-9m", label: "6–9 mo ago", from: 274, to: 182 },
  { id: "9-12m", label: "9–12 mo ago", from: 365, to: 274 },
  { id: "9m+", label: "9+ mo ago", from: null, to: 274 },
  { id: "any", label: "Any time", from: null, to: null },
];
const daysAgoIso = (d: number) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

/* Client-side mirror of the PUBLIC seed rows in supabase_schema_leadradar.sql
   — the last-resort sources display when neither the source_status view nor
   the lead_sources table is reachable (signed out, or schema not applied).
   Status is always "unknown" here: this mirror can DESCRIBE the registry but
   never assert run history it can't see. Internal (Kodiak-prospecting)
   sources are deliberately absent — they never render for customers. */
const STATIC_SOURCES = [
  { id: "hillsborough_pool_permits", label: "Hillsborough pool permits", region: "Hillsborough Co., FL", county: "hillsborough", state: "FL", lead_type: "pool_permit", adapter: "accela", automated: true, visibility: "public", enabled: true, status: "unknown", last_run_at: null, last_run_rows: null, compliance_note: "FL §515.27 gives owners four ways to pass the safety inspection — a barrier is one option. Never pitch a fence as legally required." },
  { id: "pasco_pool_permits", label: "Pasco pool permits", region: "Pasco Co., FL", county: "pasco", state: "FL", lead_type: "pool_permit", adapter: "accela", automated: true, visibility: "public", enabled: false, status: "unknown", last_run_at: null, last_run_rows: null, compliance_note: "Same §515.27 rule as Hillsborough." },
  { id: "pinellas_pool_permits", label: "Pinellas pool permits", region: "Pinellas Co., FL", county: "pinellas", state: "FL", lead_type: "pool_permit", adapter: "accela", automated: true, visibility: "public", enabled: false, status: "unknown", last_run_at: null, last_run_rows: null, compliance_note: "Same §515.27 rule. Before enabling: confirm HB 803 / FS 553.79 exemptions do not thin pool-permit volume." },
  // the concrete service's public seeds (§13 item 20) — shown under the Concrete lens
  { id: "hillsborough_concrete_permits", label: "Hillsborough driveway & flatwork permits", region: "Hillsborough Co., FL", county: "hillsborough", state: "FL", lead_type: "concrete_permit", adapter: "accela", automated: true, visibility: "public", enabled: false, status: "unknown", last_run_at: null, last_run_rows: null, compliance_note: "A flatwork permit names the work. Pitch the timing — new concrete is sealed after its 28-day cure; the settled slab beside it is a lift, not a replacement." },
  { id: "hillsborough_settling_homes", label: "Hillsborough settling-age homes (15–30 yrs)", region: "Hillsborough Co., FL", county: "hillsborough", state: "FL", lead_type: "aging_slab", adapter: "parcel_age", automated: true, visibility: "public", enabled: false, status: "unknown", last_run_at: null, last_run_rows: null, compliance_note: "An age, not a complaint. Pitch the walk-through, never a problem you have not seen." },
];

/* "SMITH JOHN & MARY" (appraiser casing) → "Smith John & Mary" */
function titleCase(s: any) {
  return String(s || "").toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()).replace(/\bLlc\b/g, "LLC");
}
const fmtDate = (d: any) => d ? new Date(d + (String(d).length === 10 ? "T12:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

/* is this available lead in the open pool (unclaimed 7+ days → quota-free)? */
function isOpenPool(lead: any) {
  const days = (window as any).VFP_RADAR?.OPEN_POOL_DAYS || 7;
  return lead.status === "available" && lead.ingested_at && (Date.now() - new Date(lead.ingested_at).getTime()) > days * 86400000;
}

/* ---- heat: when is this lead actually ready to buy a fence? --------------
   A pool takes 60–120 days to build and the fence goes in LAST, after
   equipment access closes out. A permit filed last week is a construction
   site; one filed 75 days ago is a job about to be ready. The feed sorts on
   this, not on date — judgment, not a database dump. Shown as a band, never
   a number. (Applied to permit-type leads; closings/new-construction are
   already inside their propensity window when ingested, so they ride a flat
   mid heat and sort among the warm ones by recency.) */
function heatOf(lead: any) {
  // the curves live in lib/radar.lens.ts heatFor — one per lead type (pool build, concrete cure, a home's age)
  const h = heatFor(lead);
  return { score: h.score, days: h.days, label: h.label, color: h.tone };
}
function HeatChip({ lead }: any) {
  const h = heatOf(lead);
  const t = radarType(lead.source);
  const title = t.id === "concrete_permit" ? `${h.days} days since filed — new concrete is sealed or coated after its 28-day cure (60–150 days out is the window)`
    : t.id === "aging_slab" ? `built ${builtYearOf(lead) || "—"} — slabs settle most between 18 and 30 years in`
    : `${h.days} days since ${sourceMeta(lead).verb} — fences go in at the END of a pool build (60–110 days out is the buying window)`;
  return <span className="chip" title={title} style={{ color: h.color, fontSize: 10.5, fontWeight: 700 }}>{h.label}</span>;
}
function hoursLeft(iso: any) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / HOUR));
}

/* fence_status → honest badge (unknown is the default and says so) */
function FenceBadge({ lead }: any) {
  const map: any = {
    none: { label: "No fence in imagery", color: "var(--teal)" },
    partial: { label: "Partial fence", color: "var(--amber)" },
    full: { label: "Fenced already", color: "var(--fg-3)" },
    unknown: { label: "Fence unknown", color: "var(--fg-faint)" },
  };
  const m = map[lead.fence_status] || map.unknown;
  const img = lead.imagery_date ? ` · imagery ${new Date(lead.imagery_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}` : "";
  return <span className="chip" title={"From aerial imagery — a permit filed last week won't show its pool in any imagery yet" + img} style={{ color: m.color, fontSize: 10.5 }}>{m.label}{img}</span>;
}

/* the fence footage range — or, under Concrete, the home's age (lib/radar.lens.ts) */
function estLabel(lead: any, lens?: any) { return estLabelFor(lead, lens); }

/* Sample rows for local/signed-out mode — the pitch is visible before the
   pipeline runs. Clearly fictional and clearly labeled. */
const SAMPLE_LEADS = [
  { id: "sample-1", sample: true, county: "hillsborough", state: "FL", source: "pool_permit", permit_number: "BLD-2026-04XXXX", permit_date: new Date(Date.now() - 3 * 86400000).toISOString().slice(0, 10), permit_type: "Residential Pool", description: "IN-GROUND POOL AND SPA", site_address: "1412 Sandpiper Ct", city: "Tampa", zip: "33604", owner_name: "SAMPLE HOMEOWNER", est_fence_lf_low: 170, est_fence_lf_high: 205, perimeter_ft: 214, fence_status: "none", status: "available", lat: 27.995, lng: -82.451 },
  { id: "sample-2", sample: true, county: "hillsborough", state: "FL", source: "pool_permit", permit_number: "BLD-2026-04XXXX", permit_date: new Date(Date.now() - 5 * 86400000).toISOString().slice(0, 10), permit_type: "Residential Pool", description: "NEW SWIMMING POOL W/ PAVER DECK", site_address: "8203 Cypress Bend Dr", city: "Riverview", zip: "33578", owner_name: "SAMPLE HOMEOWNER", est_fence_lf_low: 230, est_fence_lf_high: 280, perimeter_ft: 296, fence_status: "unknown", status: "available", lat: 27.866, lng: -82.326 },
  { id: "sample-3", sample: true, county: "brown", state: "WI", source: "closing", permit_number: "2026R-0XXXX", permit_date: new Date(Date.now() - 12 * 86400000).toISOString().slice(0, 10), permit_type: "Brown Co. recent closings (RETR)", description: null, site_address: "1808 Ridgeway Dr", city: "Howard", zip: "54313", owner_name: "SAMPLE BUYER", signal_amount: 412500, est_fence_lf_low: 240, est_fence_lf_high: 290, perimeter_ft: 306, fence_status: "unknown", status: "available", lat: 44.556, lng: -88.096 },
  { id: "sample-4", sample: true, county: "hillsborough", state: "FL", source: "pool_permit", permit_number: "BLD-2026-04XXXX", permit_date: new Date(Date.now() - 9 * 86400000).toISOString().slice(0, 10), permit_type: "Residential Pool", description: "RESIDENTIAL POOL", site_address: "512 Lakeshore Blvd", city: "Brandon", zip: "33511", owner_name: "SAMPLE HOMEOWNER", est_fence_lf_low: 150, est_fence_lf_high: 185, perimeter_ft: 201, fence_status: "none", status: "claimed", lat: 27.937, lng: -82.286 },
  // aged rows so the record SEARCH demo has something in its 3–6 / 6–9 month
  // windows — the "reach the household that moved in 7 months ago" pitch
  { id: "sample-5", sample: true, county: "brown", state: "WI", source: "closing", permit_number: "2026R-0XXXX", permit_date: new Date(Date.now() - 213 * 86400000).toISOString().slice(0, 10), permit_type: "Brown Co. recent closings (RETR)", description: null, site_address: "2417 Sunnyside Ln", city: "De Pere", zip: "54115", owner_name: "SAMPLE BUYER", signal_amount: 368000, est_fence_lf_low: 210, est_fence_lf_high: 255, perimeter_ft: 268, fence_status: "unknown", status: "available", lat: 44.437, lng: -88.078 },
  { id: "sample-6", sample: true, county: "hillsborough", state: "FL", source: "pool_permit", permit_number: "BLD-2025-11XXXX", permit_date: new Date(Date.now() - 152 * 86400000).toISOString().slice(0, 10), permit_type: "Residential Pool", description: "IN-GROUND POOL", site_address: "3306 Mangrove Way", city: "Valrico", zip: "33596", owner_name: "SAMPLE HOMEOWNER", est_fence_lf_low: 185, est_fence_lf_high: 225, perimeter_ft: 240, fence_status: "unknown", status: "available", lat: 27.919, lng: -82.236 },
  // the concrete service's samples (the Concrete lens): a driveway permit in its seal window, a sidewalk order just filed, a settling-age home
  { id: "sample-7", sample: true, county: "brown", state: "WI", source: "concrete_permit", permit_number: "ROW-26-0XXX", permit_date: new Date(Date.now() - 75 * 86400000).toISOString().slice(0, 10), permit_type: "Driveway / Right of Way", description: "REPLACE DRIVEWAY AND APRON — 640 SF", site_address: "2140 Cardinal Ln", city: "Howard", zip: "54313", owner_name: "SAMPLE HOMEOWNER", parcel_area_sqft: 9800, fence_status: "unknown", status: "available", lat: 44.549, lng: -88.102 },
  { id: "sample-8", sample: true, county: "brown", state: "WI", source: "concrete_permit", permit_number: "SW-26-0XXX", permit_date: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10), permit_type: "Sidewalk repair order", description: "CITY SIDEWALK REPLACEMENT — 3 FLAGS", site_address: "615 S Oneida St", city: "Green Bay", zip: "54304", owner_name: "SAMPLE HOMEOWNER", parcel_area_sqft: 6400, fence_status: "unknown", status: "available", lat: 44.503, lng: -88.045 },
  { id: "sample-9", sample: true, county: "brown", state: "WI", source: "aging_slab", permit_number: "HB-XXX-XX", permit_date: "2004-07-01", permit_type: "Home built 2004", description: "Built 2004 (22 yrs) · 8,400 sqft lot", site_address: "1319 Meadow Ridge Ct", city: "De Pere", zip: "54115", owner_name: "SAMPLE HOMEOWNER", parcel_area_sqft: 8400, fence_status: "unknown", status: "available", lat: 44.432, lng: -88.09 },
];

/* ---------------- draft quote from a claimed lead ----------------
   The mechanic that makes this a product instead of a CSV: the lead lands as
   a quote already built — footage from the parcel, priced at YOUR rates, one
   tap to open and send. Mirrors the Quick-add estimate flow. */
function DraftQuoteModal({ lead, claim, store, go, lens, onClose }: any) {
  const concrete = lensOf(lens) === "concrete";
  const products = window.VFP.PRODUCTS || [];
  const mid = lead.est_fence_lf_low > 0 ? Math.round((lead.est_fence_lf_low + (lead.est_fence_lf_high || lead.est_fence_lf_low)) / 2) : "";
  const [f, setF] = React.useState<any>({ productId: (products[0] || {}).id, lf: String(mid || ""), gates: "1", jobType: concreteStarter(lead) });
  const [busy, setBusy] = React.useState<any>(false);
  const set = (patch: any) => setF((x: any) => ({ ...x, ...patch }));
  const product = products.find((p: any) => p.id === f.productId) || products[0];
  const lfNum = Math.max(0, Number(f.lf) || 0);
  const gates = Math.max(0, Math.round(Number(f.gates) || 0));
  let computed: any = null;
  try { if (!concrete && product && lfNum > 0) computed = window.VFP.computeQuote({ linearFeet: lfNum, gates, manual: true }, product, store.settings || {}); } catch (e) {}
  const address = [lead.site_address, lead.city].filter(Boolean).join(", ");
  const when = fmtDate(lead.permit_date), county = countyLabel(lead.county);
  const canCreate = concrete ? !!f.jobType : (lfNum > 0 && !!product);

  async function create() {
    if (!canCreate || busy) return;
    // plan cap: a draft quote is a new estimate — past the month's, ask to upgrade
    if (estimateRoom(store).atLimit) { limitPrompt(store, go, "estimates"); return; }
    setBusy(true);
    let q: any;
    if (concrete) {
      // a concrete lead opens as an ESTIMATE on the job the signal suggests, the surface read from the
      // record and nothing measured yet (lib/radar.lens.ts concreteDraftForm) — the estimator takes it from there
      const base: any = buildConcreteQuote(concreteDraftForm(lead, f.jobType), store.settings || {}, {
        customer: titleCase(lead.owner_name) || "Homeowner", phone: "", email: "",   // deliberately blank: Lead Radar ships address + owner name only (no phone append — TCPA)
        address, notes: radarQuoteNote(lead, { when, county }),
      });
      const { result: _r, ...rec } = base;
      q = { id: newId("q"), status: "draft", createdAt: Date.now(), source: "leadradar", radarPermitId: lead.id, ...rec };
    } else {
      q = {
        id: newId("q"),
        customer: titleCase(lead.owner_name) || "Homeowner",
        phone: "", email: "",           // deliberately blank: Lead Radar ships address + owner name only (no phone append — TCPA)
        address,
        lf: lfNum, productId: product.id, productName: product.name,
        total: computed ? computed.customerTotal : 0,
        status: "pending", createdAt: Date.now(),
        source: "leadradar", radarPermitId: lead.id,
        inputs: { linearFeet: lfNum, gates, manual: true,   // the doc reads inputs.quoteNotes
          quoteNotes: `Lead Radar: ${sourceMeta(lead).label.toLowerCase()} ${lead.permit_number} ${sourceMeta(lead).verb} ${when} (${county} County, ${lead.state || "FL"}). Footage estimated from the parcel — verify on site.` },
      };
    }
    if (!store.addQuote(q)) { setBusy(false); return; }
    store.addActivity(concrete ? `Draft estimate from Lead Radar — ${address}` : `Draft quote from Lead Radar — ${address} · ${LR_money0(q.total)}`);
    try { if (claim && !lead.sample) await (window as any).VFP_RADAR.linkQuote(claim.id, q.id); } catch (e) { /* the quote exists either way */ }
    setBusy(false);
    onClose();
    if (concrete) go("estimator", { quoteId: q.id }); else go("quote", { quoteId: q.id });
  }

  return (
    <Portal>
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(520px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>{concrete ? "Draft estimate" : "Draft quote"} — {address}</h3>
        {concrete ? (<>
          <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
            Opens in the concrete estimator on the job this signal suggests, with the surface read from the record. Nothing is measured yet — read their photos or measure on site, then price.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <label className="field-label">Start on</label>
              <div className="row gap-6" style={{ flexWrap: "wrap" }} data-tour="radar-cx-jobs">
                {CONCRETE_JOB_CHOICES.map((c) => <button key={c.id} className={"filter-chip" + (f.jobType === c.id ? " on" : "")} onClick={() => set({ jobType: c.id })}>{c.label}</button>)}
              </div>
            </div>
            <span className="muted" style={{ fontSize: 12.5 }}>Signal: {sourceMeta(lead).label} · {sourceMeta(lead).verb} {builtYearOf(lead) || when}{lead.description ? <> · {lead.description}</> : null}</span>
          </div>
        </>) : (<>
          <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
            Footage prefilled from the parcel{estLabel(lead, lens) ? <> (<b>{estLabel(lead, lens)}</b> — verify on site)</> : null}. Pick material &amp; height; the price renders at your rates.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div style={{ flex: "2 1 220px" }}><label className="field-label">Product</label>
                <select className="select" value={f.productId || ""} onChange={(e) => set({ productId: e.target.value })}>
                  {products.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
              <div><label className="field-label">Linear ft</label>
                <input className="input tnum" type="number" min="1" style={{ width: 100 }} value={f.lf} onChange={(e) => set({ lf: e.target.value })} /></div>
              <div><label className="field-label">Gates</label>
                <input className="input tnum" type="number" min="0" style={{ width: 80 }} value={f.gates} onChange={(e) => set({ gates: e.target.value })} /></div>
            </div>
            {computed && <span className="muted" style={{ fontSize: 12.5 }}>
              Price at your rates: <b className="tnum" style={{ color: "var(--teal)" }}>{LR_money0(computed.customerTotal)}</b>
              {lfNum > 0 && <> · {LR_money0(computed.customerTotal / lfNum)}/ft</>}
            </span>}
          </div>
        </>)}
        <div className="row gap-8" style={{ marginTop: 14 }}>
          <button className="btn btn-primary btn-sm" disabled={!canCreate || busy} onClick={create}><Icon name="send" size={13} />{busy ? "Creating…" : concrete ? "Open draft estimate" : "Create draft quote"}</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

/* ---------------- lead detail (map + claim + outcome) ---------------- */
function LeadDetail({ lead, claim, quota, premium, store, go, lens, busy, onClaim, onRelease, onOutcome, onDraft, onClose }: any) {
  const concrete = lensOf(lens) === "concrete";
  const mapRef = React.useRef<any>(null);
  const mapObj = React.useRef<any>(null);
  // the feed loads without geometry (payload weight) — pull this one lead's
  // parcel ring now that its detail is open
  const [ring, setRing] = React.useState<any>(lead.polygon || null);
  React.useEffect(() => {
    let dead = false;
    if (!lead.polygon && !lead.sample) {
      (window as any).VFP_RADAR.lead(lead.id).then((full: any) => {
        if (!dead && full && Array.isArray(full.polygon)) setRing(full.polygon);
      }).catch(() => {});
    }
    return () => { dead = true; };
  }, [lead.id]);
  React.useEffect(() => {
    let dead = false;
    if (!(lead.lat && lead.lng) || !mapRef.current) return;
    ensureLeaflet().then((L: any) => {
      if (dead || !mapRef.current) return;
      const m = L.map(mapRef.current, { attributionControl: false, zoomControl: true, scrollWheelZoom: false });
      mapObj.current = m;
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 21, maxNativeZoom: 20 }).addTo(m);
      if (Array.isArray(ring) && ring.length >= 3) {
        const poly = L.polygon(ring, { color: "#22e3d4", weight: 2, fillOpacity: 0.08 }).addTo(m);
        m.fitBounds(poly.getBounds().pad(0.25));
      } else {
        m.setView([lead.lat, lead.lng], 18);
        L.circleMarker([lead.lat, lead.lng], { radius: 7, color: "#22e3d4" }).addTo(m);
      }
    }).catch(() => {});
    return () => { dead = true; try { mapObj.current && mapObj.current.remove(); } catch (e) {} };
  }, [lead.id, ring]);

  const mine = claim && !claim.released_at;
  const openPool = isOpenPool(lead);
  const left = mine ? hoursLeft(claim.expires_at) : null;
  const claimedByOther = lead.status === "claimed" && !mine;
  const freePlan = quota && quota.quota === 0;
  const outOfClaims = quota && quota.quota != null && quota.remaining === 0 && !openPool;
  const est = estLabel(lead, lens);

  return (
    <Portal>
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(680px, 96vw)", maxHeight: "92vh", overflowY: "auto" }} onClick={(e: any) => e.stopPropagation()}>
        <div className="row between" style={{ alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h3 className="display" style={{ fontSize: 19, margin: "0 0 2px" }}>{lead.site_address}{lead.city ? `, ${lead.city}` : ""}</h3>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {sourceMeta(lead).label} · {countyLabel(lead.county)} County{lead.state && lead.state !== "FL" ? `, ${lead.state}` : ""} · {lead.permit_number} · {sourceMeta(lead).verb} {builtYearOf(lead) || fmtDate(lead.permit_date)}
              {lead.owner_name ? <> · {titleCase(lead.owner_name)}</> : null}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        {(lead.lat && lead.lng) ? <div ref={mapRef} style={{ height: 240, borderRadius: 10, border: "1px solid var(--line)", margin: "12px 0" }} />
          : <div className="card pad muted" style={{ margin: "12px 0", fontSize: 12.5 }}>No parcel match for this address yet — the record is real, the map join just didn't land.</div>}

        <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 10 }}>
          <HeatChip lead={lead} />
          {est && (concrete
            ? <span className="chip teal" title="From the property-appraiser roll — an age is the signal, not a diagnosis">{est}</span>
            : <span className="chip teal" title="Computed from the parcel perimeter — a range on purpose. Walk the property before you commit a number.">{est} · verify on site</span>)}
          {lead.signal_amount > 0 && <span className="chip" style={{ color: "var(--green)", fontSize: 10.5 }}>{LR_money0(lead.signal_amount)} {lead.source === "closing" ? "sale" : "project"}</span>}
          {!concrete && <FenceBadge lead={lead} />}
          {lead.parcel_area_sqft > 0 && <span className="chip" style={{ fontSize: 10.5 }}>{Math.round(lead.parcel_area_sqft).toLocaleString()} sqft lot</span>}
          {premium && <span className="chip" style={{ color: "var(--violet)", fontSize: 10.5 }} title="Ingested from a paid data vendor — claims are capped monthly and need an active paid plan">
            Premium data{quota && quota.premium_quota > 0 ? ` · ${quota.premium_remaining} of ${quota.premium_quota} left this month` : ""}</span>}
          {openPool && lead.status === "available" && <span className="chip" style={{ color: "var(--amber)", fontSize: 10.5 }}>{premium ? "Open pool — premium cap still applies" : "Open pool — claiming is quota-free"}</span>}
        </div>
        {(lead.permit_type || lead.description) && <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>{[radarType(lead.source).kind === "age" ? null : lead.permit_type, lead.description].filter(Boolean).join(" — ")}</p>}

        <div className="card pad" style={{ background: "var(--surface-2)", marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>{sourceMeta(lead).why(lead)}</div>
        </div>

        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
          {lead.sample ? <span className="chip" style={{ color: "var(--amber)" }}>Sample lead — sign in with the live pipeline to claim</span>
            : mine ? (<>
              <span className="chip teal"><Icon name="check" size={12} />Yours{claim.quote_id || claim.outcome ? "" : left != null ? ` · ${left}h exclusive left` : ""}</span>
              <button className="btn btn-primary btn-sm" onClick={onDraft}><Icon name="send" size={13} />{concrete ? "Build draft estimate" : "Build draft quote"}</button>
              {!claim.outcome && claim.quote_id && <span className="muted" style={{ fontSize: 12 }}>quote attached</span>}
              <span style={{ flex: 1 }} />
              {["won", "lost", "no_contact"].map((o) => (
                <button key={o} className={"btn btn-sm " + (claim.outcome === o ? "btn-primary" : "btn-outline")} disabled={busy} title="Outcome tracking — confirmed closed jobs is the number that decides whether Lead Radar lives" onClick={() => onOutcome(claim, o)}>
                  {o === "won" ? "Won" : o === "lost" ? "Lost" : "No contact"}
                </button>
              ))}
              <button className="btn btn-ghost btn-sm" disabled={busy} title="Put the lead back in the pool (your monthly claim is not refunded)" onClick={() => onRelease(lead)}>Release</button>
            </>)
            : claimedByOther ? <span className="chip" style={{ color: "var(--fg-3)" }}><Icon name="lock" size={12} />Claimed by another contractor{lead.claim_expires_at && hoursLeft(lead.claim_expires_at) != null ? ` · window ends in ${hoursLeft(lead.claim_expires_at)}h` : ""}</span>
            : lead.status === "expired" ? <span className="chip" title="Its buying window has closed — kept in the record archive for reference, not claimable">Aged out of its window</span>
            : premium && quota && quota.paying === false ? (<>
              {/* paid-data leads cost real money to ingest: active paid plans
                  only — no trial, no free, and the open pool doesn't exempt */}
              <span className="muted" style={{ fontSize: 12.5 }}>Premium-data leads need an active paid plan — free and trial accounts can watch the feed.</span>
              <button className="btn btn-primary btn-sm" onClick={() => go("account", { tab: "plan" })}><Icon name="bolt" size={13} />Upgrade to claim</button>
            </>)
            : premium && quota && quota.premium_quota > 0 && quota.premium_remaining === 0 ? (<>
              <span className="muted" style={{ fontSize: 12.5 }}>All {quota.premium_quota} premium-data leads used this month — resets {quota.resets_at ? new Date(quota.resets_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "on the 1st"}.</span>
            </>)
            : freePlan && !openPool ? (<>
              <span className="muted" style={{ fontSize: 12.5 }}>Claiming leads is for paid plans — you can watch the feed free.</span>
              <button className="btn btn-primary btn-sm" onClick={() => go("account", { tab: "plan" })}><Icon name="bolt" size={13} />Upgrade to claim</button>
            </>)
            : outOfClaims ? (<>
              <span className="muted" style={{ fontSize: 12.5 }}>All {quota.quota} claims used this month — resets {quota.resets_at ? new Date(quota.resets_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "on the 1st"}.</span>
              <button className="btn btn-outline btn-sm" onClick={() => go("account", { tab: "plan" })}>Upgrade for more</button>
            </>)
            : (<>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onClaim(lead)}>
                <Icon name="target" size={13} />{busy ? "Claiming…" : "Claim this lead — 72h exclusive"}
              </button>
              {quota && quota.quota != null && !openPool && <span className="muted" style={{ fontSize: 12 }}>{quota.remaining} of {quota.quota} claims left this month</span>}
            </>)}
        </div>

        <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
          {lead.source_url && <a className="btn btn-ghost btn-sm" href={lead.source_url} target="_blank" rel="noreferrer"><Icon name="book" size={13} />{RADAR_COPY[lensOf(lens)].recordVerb}</a>}
          {lead.apn && <span className="faint" style={{ fontSize: 11, alignSelf: "center" }}>APN {lead.apn}</span>}
        </div>
      </div>
    </div>
    </Portal>
  );
}

/* ---------------- one lead row (shared by the feed and record search) ------ */
function LeadRow({ lead, mine, claim, lens, onOpen }: any) {
  const l = lead;
  const open = isOpenPool(l);
  const left = mine && claim ? hoursLeft(claim.expires_at) : null;
  return (
    <div className="proj-row" {...clickable(() => onOpen(l), { label: `Open the lead for ${l.name || l.address || "this property"}` })}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>
          {l.site_address}{l.city ? <span className="muted" style={{ fontWeight: 500 }}>, {l.city}</span> : null}
          {l.sample && <span className="chip" style={{ marginLeft: 8, fontSize: 9.5, color: "var(--amber)" }}>SAMPLE</span>}
        </div>
        <div className="muted" style={{ fontSize: 12.5, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 2 }}>
          <HeatChip lead={l} />
          <span>{sourceMeta(l).label} · {countyLabel(l.county)}{l.state && l.state !== "FL" ? ", " + l.state : ""} · {sourceMeta(l).verb} {builtYearOf(l) || fmtDate(l.permit_date)}</span>
          {l.signal_amount > 0 && <span style={{ color: "var(--green)", fontWeight: 600 }}>{LR_money0(l.signal_amount)}</span>}
          {estLabel(l, lens) && <span style={{ color: "var(--fg-2)", fontWeight: 600 }}>{estLabel(l, lens)}</span>}
          {lensOf(lens) === "fence" && <FenceBadge lead={l} />}
        </div>
      </div>
      {mine
        ? <span className="chip teal"><Icon name="check" size={12} />Yours{claim && !claim.quote_id && !claim.outcome && left != null ? ` · ${left}h` : ""}</span>
        : l.status === "claimed"
          ? <span className="chip" style={{ color: "var(--fg-3)" }}><Icon name="lock" size={12} />Claimed</span>
          : l.status === "expired"
            ? <span className="chip" style={{ color: "var(--fg-faint)" }} title="Past its buying window — off the live feed, kept in the archive for reference">Aged out</span>
            : open
              ? <span className="chip" style={{ color: "var(--amber)" }}>Open pool</span>
              : <span className="chip" style={{ color: "var(--teal)" }}>Available</span>}
      <Icon name="chevright" size={16} className="muted" />
    </div>
  );
}

/* ---------------- sources: where leads come from ----------------
   ALWAYS rendered — an empty radar with invisible plumbing is
   indistinguishable from a broken product. Three data grades, best first:
   1. source_status view rows — status COMPUTED from run history (a source
      cannot read as live without a successful run that returned rows);
   2. lead_sources registry rows (schema behind → run history unreadable):
      shown as status "unknown", never a guessed one;
   3. the STATIC_SOURCES mirror (signed out / registry unreachable): the
      built-in registry description only, labeled as such via `note`.
   Every non-producing source states WHY it isn't producing — "0 leads" with
   no visible reason is how weeks get lost. */
const fmtShort = (iso: any) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
function SourcesPanel({ rows, note }: any) {
  const [more, setMore] = React.useState<any>(false);
  const noun = (s: any) => s.lead_type === "closing" ? "closings" : s.lead_type === "new_construction" || s.lead_type === "aging_slab" ? "homes" : "permits";
  const DOT: any = { live: "var(--green)", stale: "var(--amber)", stalled: "var(--amber)", error: "var(--red)" };
  const signalLabel = (s: any) => (SOURCE_META[s.lead_type] || SOURCE_META.pool_permit).label + "s";
  const why = (s: any) => {
    if (s.status === "live") return <>Last updated {fmtShort(s.last_run_at)} · {s.last_run_rows} {noun(s)}</>;
    if (s.status === "stale") return <>Last updated {fmtShort(s.last_run_at)} · {s.last_run_rows} {noun(s)} — <span style={{ color: "var(--amber)" }}>weekly update overdue</span></>;
    if (s.status === "stalled") return <>Last run {fmtShort(s.last_run_at)} completed but found <b>0 {noun(s)}</b> — the portal answered, nothing matched the filters.</>;
    if (s.status === "error") return <span style={{ color: "var(--red)" }}>Last run failed — nothing was ingested. Fix from the ops console or server log.</span>;
    if (s.status === "never_run") return <>Enabled, but <b>the first ingest has never run</b> — this source has produced nothing yet. Start it from the ops console or <span className="mono">npm run radar -- run</span> (backfill 180d on the first Hillsborough run).</>;
    if (s.status === "planned") return <>Not enabled — produces nothing until it's switched on.</>;
    return <>Run status unavailable right now — showing the registry without history.</>;
  };
  const expanded = rows.filter((s: any) => s.status !== "planned");
  const planned = rows.filter((s: any) => s.status === "planned");
  const liveCount = rows.filter((s: any) => s.status === "live").length;
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row gap-8" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <b style={{ fontSize: 12.5 }}>Sources — where leads come from</b>
        <span className="muted" style={{ fontSize: 12 }}>{liveCount} live · {rows.filter((s: any) => s.enabled).length} enabled · {rows.length} registered</span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: "6px 0 0", maxWidth: 720 }}>
        Every lead is ingested from these public-record feeds — the radar (and record search) can only show what a source has actually pulled. Automated sources scrape weekly (Mon 6:00 ET); the rest are fed by CSV import.
      </p>
      {note && <p style={{ fontSize: 12, color: "var(--amber)", margin: "6px 0 0" }}>{note}</p>}
      <div style={{ display: "grid", gap: 9, marginTop: 12 }}>
        {expanded.map((s: any) => (
          <div key={s.id} className="row gap-8" style={{ fontSize: 12.5, flexWrap: "wrap", alignItems: "baseline" }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, flexShrink: 0, alignSelf: "center", background: DOT[s.status] || "var(--fg-faint)" }} />
            <b>{s.label}</b>
            <span className="muted">{s.region || countyLabel(s.county) + " Co., " + s.state}</span>
            <span className="chip" style={{ fontSize: 9.5 }}>{signalLabel(s)}</span>
            <span className="chip" style={{ fontSize: 9.5 }}>{s.automated ? "auto · weekly" : "manual · CSV import"}</span>
            {s.premium && <span className="chip" style={{ color: "var(--violet)", fontSize: 9.5 }} title="Paid data vendor — claims from this source are capped monthly and need an active paid plan">premium · paid data</span>}
            {s.visibility === "internal" && <span className="chip" style={{ fontSize: 9.5 }}>internal</span>}
            <span className="muted" style={{ fontSize: 11.5, flexBasis: "100%", paddingLeft: 16 }}>{why(s)}</span>
            {s.compliance_note && <span className="faint" style={{ fontSize: 11, flexBasis: "100%", paddingLeft: 16 }}>⚖ {s.compliance_note}</span>}
          </div>
        ))}
        {expanded.length === 0 && <span className="muted" style={{ fontSize: 12.5 }}>No sources registered.</span>}
        {planned.length > 0 && (
          <div>
            <button className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={() => setMore((m: any) => !m)}>
              <Icon name="chevdown" size={12} style={{ transform: more ? "none" : "rotate(-90deg)" }} />
              <span className="muted" style={{ fontSize: 12 }}>More markets coming ({planned.length})</span>
            </button>
            {more && planned.map((s: any) => (
              <div key={s.id} className="row gap-8" style={{ fontSize: 12, flexWrap: "wrap", paddingLeft: 16, marginTop: 4 }}>
                <span className="muted">{s.label}</span>
                <span className="faint">{s.region || ""} · {signalLabel(s)} · not enabled</span>
                {s.visibility === "internal" && <span className="chip" style={{ fontSize: 9.5 }}>internal</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- record search: the archive, on your terms ----------------
   The feed answers "what's hot right now"; search answers "who moved into a
   house 6–9 months ago" — signal type × date window × market × free text,
   over EVERYTHING the sources have ingested, including records the feed has
   aged out (those are view-only). Windows cut on the SIGNAL date, so
   "6–9 mo ago" means the closing/filing happened then — the classic
   first-year-fence outreach list. */
function SearchSection({ RADAR, live, lens, sourceRows, degraded, terrIds, isMine, claimFor, onOpen, version }: any) {
  const SIGNAL_OPTIONS: any[] = signalOptionsFor(lens);
  const [signal, setSignal] = React.useState<any>("all");
  const [win, setWin] = React.useState<any>("90d");
  const [county, setCounty] = React.useState<any>("all");
  const [text, setText] = React.useState<any>("");
  const [res, setRes] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [copied, setCopied] = React.useState<any>(false);
  const reqRef = React.useRef<any>(0);

  // market options: only YOUR markets when a territory is set (a WI account
  // has no use for a Florida county list), and no dropdown at all when the
  // registry is degraded or there's just one market — the text box already
  // covers city/ZIP
  const scoped = terrIds && terrIds.size > 0 ? (sourceRows || []).filter((s: any) => terrIds.has(s.id)) : (sourceRows || []);
  const counties: any[] = Array.from(new Set(scoped.map((s: any) => s.county)));
  const showMarkets = !degraded && counties.length > 1;
  React.useEffect(() => { if (county !== "all" && !counties.includes(county)) setCounty("all"); },
    [counties.join(","), county]);  // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = React.useCallback(async (p: any) => {
    const w = SEARCH_WINDOWS.find((x: any) => x.id === p.win) || SEARCH_WINDOWS[0];
    const opt = SIGNAL_OPTIONS.find((o: any) => o.id === p.signal) || SIGNAL_OPTIONS[0];
    const from = w.from != null ? daysAgoIso(w.from) : null;
    const to = w.to != null ? daysAgoIso(w.to) : null;
    const req = ++reqRef.current;
    setBusy(true); setErr("");
    try {
      let out;
      if (!live) {
        // sample mode: the same filters over the sample rows, so the search
        // pitch is visible before the pipeline runs
        const rows = sampleLeadsFor(lens, SAMPLE_LEADS).filter((l: any) =>
          (!opt.sources || opt.sources.includes(l.source)) &&
          (p.county === "all" || l.county === p.county) &&
          (!from || l.permit_date >= from) && (!to || l.permit_date < to) &&
          (!p.text || [l.site_address, l.city, l.zip, l.owner_name].join(" ").toLowerCase().includes(String(p.text).toLowerCase())));
        out = { rows, total: rows.length };
      } else {
        // "all signals" is all of THIS lens's types — a fence search never lists a driveway permit, and vice versa
        out = (await RADAR.search({ source: opt.sources || leadTypesOf(lens), county: p.county, from, to, q: p.text })) || { rows: [], total: 0 };
      }
      if (req === reqRef.current) setRes(out);
    } catch (e: any) { if (req === reqRef.current) { setErr(String(e?.message || e)); setRes(null); } }
    if (req === reqRef.current) setBusy(false);
  }, [live, lens]);

  // chip changes re-search immediately; free text searches on Enter / button.
  // `version` bumps after a claim/release so row states stay truthful.
  React.useEffect(() => { runSearch({ signal, win, county, text }); },
    [signal, win, county, version, runSearch]);  // eslint-disable-line react-hooks/exhaustive-deps

  const winLabel = (SEARCH_WINDOWS.find((x: any) => x.id === win) || ({} as any)).label;
  const anyIngested = (sourceRows || []).some((s: any) => s.status === "live" || s.status === "stale" || s.last_run_rows > 0);

  function copyList() {
    if (!res || !res.rows.length) return;
    const lines = res.rows.map((l: any) =>
      [titleCase(l.owner_name) || "Homeowner", [l.site_address, l.city, [l.state, l.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")].join(" — "));
    try { navigator.clipboard.writeText(lines.join("\n")).catch(() => {}); } catch (e) {}
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }

  return (
    <>
      <div className="cat-tabs" style={{ marginBottom: 4 }}>
        {SIGNAL_OPTIONS.map((o: any) => (
          <button key={o.id} className={"filter-chip" + (signal === o.id ? " on" : "")} title={o.title} onClick={() => setSignal(o.id)}>{o.label}</button>
        ))}
      </div>
      <div className="cat-tabs" style={{ marginBottom: 6 }}>
        {SEARCH_WINDOWS.map((w: any) => (
          <button key={w.id} className={"filter-chip" + (win === w.id ? " on" : "")} title="Cuts on the signal date — when the deed transfer closed / the permit was filed / the home was built" onClick={() => setWin(w.id)}>{w.label}</button>
        ))}
      </div>
      <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 10 }}>
        {showMarkets && (
          <select className="select" style={{ width: 170 }} value={county} onChange={(e: any) => setCounty(e.target.value)}>
            <option value="all">All markets</option>
            {counties.map((c: any) => <option key={c} value={c}>{countyLabel(c)}</option>)}
          </select>
        )}
        <input className="input" style={{ flex: "1 1 240px", maxWidth: 380 }} placeholder="Address, city, ZIP, or owner…" value={text}
          onChange={(e: any) => setText(e.target.value)}
          onKeyDown={(e: any) => { if (e.key === "Enter") runSearch({ signal, win, county, text }); }} />
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => runSearch({ signal, win, county, text })}>
          <Icon name="search" size={13} />{busy ? "Searching…" : "Search"}
        </button>
      </div>
      {err && <div className="card pad" style={{ marginBottom: 10, borderColor: "var(--red-tint)", color: "var(--red)", fontSize: 12.5 }}>{err}</div>}
      <div className="card pad">
        {!res
          ? <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>{busy ? "Searching…" : ""}</p>
          : res.rows.length === 0
            ? <EmptyState icon="search" title="No records in this window"
                sub={res.schemaOutdated
                  ? "Your database is still on the original Lead Radar schema, so closings and new-construction records can't exist in it yet. Run supabase_schema_leadradar.sql in the Supabase SQL editor (safe to re-run — it migrates in place), then refresh and search again."
                  : live && !anyIngested
                    ? "Nothing has been ingested yet — search can only reach records a source has pulled. The Sources panel below shows each feed and why it hasn't produced (most likely: the first ingest has never run)."
                    : "Try a wider date window, another signal, or clear the text filter. Search covers everything ingested for your visible markets — including records the live feed has aged out."} />
            : (<>
                <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <b style={{ fontSize: 12.5 }}>{res.total} record{res.total === 1 ? "" : "s"}</b>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {(SIGNAL_OPTIONS.find((o: any) => o.id === signal) || ({} as any)).label} · {winLabel}{county !== "all" ? " · " + countyLabel(county) : ""}
                    {res.total > res.rows.length ? ` · showing first ${res.rows.length}` : ""} · aged-out records included
                  </span>
                  <span style={{ flex: 1 }} />
                  <button className="btn btn-outline btn-sm" title="Copy owner + address lines for your mail merge — Lead Radar ships no phone numbers by design, so direct mail and door knocks are the clean plays" onClick={copyList}>
                    <Icon name="clipboard" size={13} />{copied ? "Copied ✓" : "Copy list"}
                  </button>
                </div>
                <div style={{ display: "grid", gap: 9 }}>
                  {res.rows.map((l: any) => <LeadRow key={l.id} lead={l} lens={lens} mine={isMine(l)} claim={claimFor(l)} onOpen={onOpen} />)}
                </div>
              </>)}
      </div>
    </>
  );
}

/* ---------------- territory: where do you work? ----------------
   The missing onboarding step — the feed scopes to these picks. Checking a
   not-yet-live market is a "notify me": it stores demand data for which
   metro to build next (customers pick the roadmap instead of us guessing). */
function TerritoryPicker({ coverage, territories, onSave, onClose }: any) {
  const [sel, setSel] = React.useState<any>(() => {
    const m: any = {};
    (territories || []).forEach((t: any) => { m[t.source_id] = true; });
    return m;
  });
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const isLive = (s: any) => s.status === "live" || s.status === "stale";
  const toggle = (id: any) => setSel((m: any) => ({ ...m, [id]: !m[id] }));
  async function save() {
    setBusy(true); setErr("");
    try {
      const entries = coverage.filter((s: any) => sel[s.id]).map((s: any) => ({ source_id: s.id, notify: !isLive(s) }));
      await onSave(entries);
    } catch (e: any) { setErr(String(e?.message || e)); }
    setBusy(false);
  }
  return (
    <div className="card pad" style={{ marginBottom: 14, borderColor: "var(--teal-tint-2)" }}>
      <h3 className="display" style={{ fontSize: 17, margin: "0 0 4px" }}>Where do you work?</h3>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Your feed scopes to the markets you pick. Check a coming-soon market and we'll notify you the day it goes live.</p>
      <div style={{ display: "grid", gap: 8 }}>
        {coverage.map((s: any) => (
          <label key={s.id} className="row gap-8" style={{ fontSize: 13, alignItems: "center", cursor: "pointer", flexWrap: "wrap" }}>
            <input type="checkbox" checked={!!sel[s.id]} onChange={() => toggle(s.id)} />
            <b>{s.region || s.label}</b>
            {isLive(s)
              ? <span className="chip teal" style={{ fontSize: 10 }}>live · {s.last_run_rows || 0} leads last run</span>
              : <span className="chip" style={{ color: "var(--amber)", fontSize: 10 }}>coming soon — notify me</span>}
            <span className="muted" style={{ fontSize: 11.5 }}>{s.label}</span>
          </label>
        ))}
        {coverage.length === 0 && <span className="muted" style={{ fontSize: 12.5 }}>No markets registered yet.</span>}
      </div>
      {err && <p style={{ color: "var(--red)", fontSize: 12, margin: "10px 0 0" }}>{err}</p>}
      <div className="row gap-8" style={{ marginTop: 12 }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}><Icon name="check" size={13} />{busy ? "Saving…" : "Save territory"}</button>
        {onClose && <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>}
      </div>
    </div>
  );
}

/* ---------------- the tab ---------------- */
function LeadRadarPage({ store, go }: any) {
  const RADAR: any = (window as any).VFP_RADAR;
  // the lens (lib/verticals.ts): which lead types, sources, samples and words this screen shows
  const lens = lensOf(store && store.lens);
  const copy = RADAR_COPY[lens];
  const [status, setStatus] = React.useState<any>("loading");
  const [leads, setLeads] = React.useState<any>([]);
  const [claims, setClaims] = React.useState<any>([]);
  const [quota, setQuota] = React.useState<any>(null);
  const [coverage, setCoverage] = React.useState<any>([]);   // source_status view rows
  const [registry, setRegistry] = React.useState<any>(null); // lead_sources fallback rows (schema behind)
  const [territories, setTerritories] = React.useState<any>(null);  // null = feature unavailable/unknown
  const [showTerritory, setShowTerritory] = React.useState<any>(false);
  const [county, setCounty] = React.useState<any>("all");
  const [srcFilter, setSrcFilter] = React.useState<any>("all");
  const [tab, setTab] = React.useState<any>("feed");         // feed | search
  const [view, setView] = React.useState<any>("all");        // all | available | mine
  const [sort, setSort] = React.useState<any>("heat");       // heat | new
  const [sel, setSel] = React.useState<any>(null);           // lead in the detail modal
  const [draft, setDraft] = React.useState<any>(null);       // lead in the draft-quote modal
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [version, setVersion] = React.useState<any>(0);      // bumps after claim/release → search re-runs
  const [outdated, setOutdated] = React.useState<any>(false); // DB still on the ORIGINAL pool_permits shape

  const load = React.useCallback(async () => {
    if (!RADAR || !RADAR.enabled()) { setStatus("off"); return; }
    try {
      const st = await RADAR.status();
      setStatus(st);
      if (st !== "ready") return;
      const [ls, cs, qt, sr, tr] = await Promise.all([RADAR.leads(undefined, leadTypesOf(lens)), RADAR.myClaims(), RADAR.quota(), RADAR.sources(), RADAR.territories()]);
      if (ls) setLeads(ls);
      setClaims(cs || []);
      setQuota(qt);
      setCoverage(sr);      // null = source_status view missing (schema behind)
      setTerritories(tr);   // null = account_territories missing (schema behind)
      // schema behind → statuses unreadable, but the REGISTRY usually isn't:
      // fall back to lead_sources so the page can still show what the sources
      // are (status "unknown", never guessed)
      if (sr === null) { try { setRegistry(await RADAR.sourcesRegistry()); } catch (e) { setRegistry(null); } }
      else setRegistry(null);
      // the leads() call above ran the column probe — if it found the ORIGINAL
      // pool_permits shape, say that exactly instead of showing a quiet zero
      setOutdated(!!(RADAR.schemaOutdated && RADAR.schemaOutdated()));
    } catch (e: any) { setStatus("error"); setErr(String(e?.message || e)); }
  }, [lens]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);

  const live = status === "ready";
  // core tables answered but the ops-layer tables/views didn't → the DB is one
  // schema run behind; say so loudly instead of silently hiding features
  const schemaBehind = live && (territories === null || coverage === null);
  const cov = (coverage || []).filter((s: any) => sourceInLens(s, lens));
  // the sources panel always has SOMETHING honest to show: computed statuses
  // (best) → registry rows without history → the built-in registry mirror —
  // this lens's sources only
  const srcRows = live && coverage !== null ? cov : (live && registry ? registry : STATIC_SOURCES).filter((s: any) => sourceInLens(s, lens));
  const srcNote = !live
    ? "Showing the built-in registry — sign in with the live database for run status."
    : coverage !== null ? ""
    : registry ? "Run status is hidden until the schema update above is applied — the registry is shown without history."
    : "Showing the built-in registry — the live registry couldn't be read until the schema update above is applied.";
  // leads from paid-data sources: capped monthly, active-paid-plan-only
  const premiumIds = new Set(srcRows.filter((s: any) => s.premium).map((s: any) => s.id));
  const claimByPermit = React.useMemo(() => {
    const m = new Map();
    (claims || []).forEach((c: any) => { const cur = m.get(c.permit_id); if (!cur || !c.released_at) m.set(c.permit_id, c); });
    return m;
  }, [claims]);
  const isMine = (l: any) => { const c = claimByPermit.get(l.id); return !!(c && !c.released_at); };

  // territory scoping: with a territory set, the feed is those markets plus
  // anything you've claimed (a claim survives a territory edit)
  // …and only THIS lens's sources count: a fence territory must neither scope the
  // concrete feed nor be dropped when the concrete picker saves (setTerritories
  // replaces the whole list — the other service's rows ride along on every save)
  const lensSourceIds = new Set(srcRows.map((s: any) => s.id));
  const terrIds = new Set((territories || []).filter((t: any) => !t.notify && lensSourceIds.has(t.source_id)).map((t: any) => t.source_id));
  const territorySet = terrIds.size > 0;
  const saveTerritories = async (entries: any[]) => {
    const other = (territories || []).filter((t: any) => !lensSourceIds.has(t.source_id)).map((t: any) => ({ source_id: t.source_id, notify: !!t.notify }));
    await RADAR.setTerritories([...other, ...(entries || [])]);
  };
  const allRows = (live ? leads : sampleLeadsFor(lens, SAMPLE_LEADS)).filter((l: any) => leadInLens(l, lens));
  const rows = live && territorySet ? allRows.filter((l: any) => isMine(l) || terrIds.has(l.source_id)) : allRows;

  const counties = Array.from(new Set(rows.map((l: any) => l.county)));
  const sourcesInFeed = Array.from(new Set(rows.map((l: any) => l.source || "pool_permit")));
  const filtered = rows
    .filter((l: any) => county === "all" || l.county === county)
    .filter((l: any) => srcFilter === "all" || (l.source || "pool_permit") === srcFilter)
    .filter((l: any) => view === "all" ? true : view === "mine" ? isMine(l) : l.status === "available")
    // default sort is HEAT, not date: the top of the feed is the lead whose
    // pool build is entering fence season, not whoever filed last
    .sort((a: any, b: any) => sort === "heat"
      ? (heatOf(b).score - heatOf(a).score) || (heatOf(b).days - heatOf(a).days)
      : String(b.permit_date).localeCompare(String(a.permit_date)));

  const weekAgo = Date.now() - 7 * 86400000;
  const newThisWeek = rows.filter((l: any) => new Date(l.permit_date + "T12:00:00").getTime() >= weekAgo).length;
  const availableNow = rows.filter((l: any) => l.status === "available").length;
  const myActive = (claims || []).filter((c: any) => !c.released_at).length;
  // quota meter: a genuinely-unlimited plan shows usage (never "∞" — scarcity
  // is the retention mechanic, and ∞ reads as "no reason to log in tomorrow");
  // quota.fallback = plan-derived estimate (RPC unreachable) — marked "≈"
  const unlimited = quota && quota.quota == null;
  const claimsLeft = !quota ? "—" : unlimited ? (quota.used || 0) : (quota.fallback ? "≈" : "") + quota.remaining;
  const claimsLabel = unlimited ? "Claims Used · Unlimited Plan" : "Claims Left This Month";

  async function doClaim(lead: any) {
    setBusy(true); setErr("");
    try {
      await RADAR.claim(lead.id);
      await load();
      store.addActivity(`Claimed Lead Radar lead — ${lead.site_address}`);
      setSel((s: any) => s && { ...s, status: "claimed" });
      setVersion((v: any) => v + 1);
    } catch (e: any) { setErr(String(e?.message || e)); }
    setBusy(false);
  }
  async function doRelease(lead: any) {
    if (!(await confirmDialog({ title: "Release this lead back to the pool?", body: "Your monthly claim is not refunded.", confirmLabel: "Release lead", danger: true }))) return;
    setBusy(true); setErr("");
    try { await RADAR.release(lead.id); await load(); setSel(null); setVersion((v: any) => v + 1); }
    catch (e: any) { setErr(String(e?.message || e)); }
    setBusy(false);
  }
  async function doOutcome(claim: any, outcome: any) {
    setBusy(true); setErr("");
    try {
      await RADAR.setOutcome(claim.id, outcome);
      if (outcome === "won") store.addActivity("🎉 Lead Radar job WON — that's the number that matters");
      await load();
    } catch (e: any) { setErr(String(e?.message || e)); }
    setBusy(false);
  }

  const selClaim = sel ? claimByPermit.get(sel.id) : null;

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 6, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Lead Radar <span className="chip teal" style={{ verticalAlign: "middle", marginLeft: 6 }}>BETA</span></h1>
          <p className="muted" style={{ margin: "4px 0 0", maxWidth: 640 }}>{copy.subtitle}</p>
        </div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {live && territories !== null && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowTerritory((t: any) => !t)} title="Which markets your feed watches">
              <Icon name="mappin" size={13} />
              {territorySet ? "Territory: " + Array.from(terrIds).map((id: any) => { const s = cov.find((x: any) => x.id === id); return s ? countyLabel(s.county) : id; }).join(", ") : "Set territory"}
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={load} title="Refresh the feed"><Icon name="undo" size={13} />Refresh</button>
        </div>
      </div>

      {!live && status !== "loading" && (
        <div className="card pad" style={{ margin: "10px 0 14px", borderColor: "var(--teal-tint-2)", fontSize: 12.5 }}>
          {status === "off" && <><b>Sample preview.</b> Sign in to see the live feed — leads below are illustrative.</>}
          {status === "no-schema" && <><b>Sample preview.</b> The Lead Radar tables aren't in your database yet — run <span className="mono">supabase_schema_leadradar.sql</span> in the Supabase SQL editor, then start the ingest (see <span className="mono">LEADRADAR.md</span>).</>}
          {status === "error" && <><b>Sample preview.</b> Couldn't reach the lead feed{err ? ` — ${err}` : ""}. <button className="btn btn-ghost btn-sm" onClick={load}>Retry</button></>}
        </div>
      )}
      {err && live && <div className="card pad" style={{ margin: "10px 0 14px", borderColor: "var(--red-tint)", color: "var(--red)", fontSize: 12.5 }}>{err}</div>}
      {live && outdated && (
        <div className="card pad" style={{ margin: "10px 0 14px", borderColor: "var(--amber)", fontSize: 12.5 }}>
          <b style={{ color: "var(--amber)" }}>Found it — your database is still on the original Lead Radar schema.</b> The columns for newer signal types (recent closings, new construction), the sources registry, and run history don't exist in it yet — that's why the radar has sat at zero and searches came back empty. The page is running in permit-only compatibility mode right now. The fix takes two minutes: run <span className="mono">supabase_schema_leadradar.sql</span> in the Supabase SQL editor (safe to re-run — it migrates in place, nothing is lost), then refresh this page.
        </div>
      )}
      {schemaBehind && !outdated && (
        <div className="card pad" style={{ margin: "10px 0 14px", borderColor: "var(--teal-tint-2)", fontSize: 12.5 }}>
          <b>One step left:</b> your database is a schema update behind, so territory setup and live coverage status are hidden. Re-run <span className="mono">supabase_schema_leadradar.sql</span> in the Supabase SQL editor (safe to re-run — it migrates in place, nothing is lost), then refresh this page.
        </div>
      )}

      <div className="stat-row" style={{ margin: "14px 0 18px" }}>
        <StatCard icon="mappin" value={newThisWeek} label="New This Week" accent="var(--teal)" />
        <StatCard icon="target" value={availableNow} label="Available Now" accent="var(--cyan)" />
        <StatCard icon="check" value={myActive} label="My Active Claims" accent="var(--violet)" />
        <StatCard icon="bolt" value={claimsLeft} label={claimsLabel} accent="var(--amber)" />
      </div>

      {live && showTerritory && territories !== null && (
        <TerritoryPicker coverage={cov} territories={territories}
          onClose={() => setShowTerritory(false)}
          onSave={async (entries: any) => { await saveTerritories(entries); setShowTerritory(false); await load(); }} />
      )}

      {/* the two ways in: the curated heat feed, and record search over the
          full ingested archive (signal × date window — incl. 6–9 mo ago) */}
      <div className="cat-tabs" style={{ marginBottom: 8 }}>
        <button className={"filter-chip" + (tab === "feed" ? " on" : "")} onClick={() => setTab("feed")}><Icon name="target" size={12} />Radar feed</button>
        <button className={"filter-chip" + (tab === "search" ? " on" : "")}
          title={copy.searchTitle}
          onClick={() => setTab("search")}><Icon name="search" size={12} />Search records</button>
      </div>

      {tab === "search" ? (
        <SearchSection RADAR={RADAR} live={live} lens={lens} sourceRows={srcRows}
          degraded={!live || coverage === null} terrIds={terrIds} isMine={isMine}
          claimFor={(l: any) => claimByPermit.get(l.id)}
          onOpen={(l: any) => { setErr(""); setSel(l); }} version={version} />
      ) : (<>
      <div className="cat-tabs" style={{ marginBottom: 6 }}>
        {[["all", "All leads"], ["available", "Available"], ["mine", "My claims"]].map(([id, lb]) => (
          <button key={id} className={"filter-chip" + (view === id ? " on" : "")} onClick={() => setView(id)}>{lb}</button>
        ))}
        <span style={{ width: 14 }} />
        {[["heat", "Hottest first"], ["new", "Newest"]].map(([id, lb]) => (
          <button key={"o" + id} className={"filter-chip" + (sort === id ? " on" : "")} title={copy.sortTitle} onClick={() => setSort(id)}>{lb}</button>
        ))}
        {/* source and county are pickers, not chip rows — each market adds
            counties, and a row per filter pushed the first lead off screen */}
        {(sourcesInFeed.length > 1 || counties.length > 1) && <span style={{ width: 14 }} />}
        {sourcesInFeed.length > 1 && (
          <select className="select radar-pick" aria-label="Lead source" value={srcFilter} onChange={e => setSrcFilter(e.target.value)}>
            <option value="all">All sources</option>
            {sourcesInFeed.map((s: any) => <option key={s} value={s}>{(SOURCE_META[s] || SOURCE_META.pool_permit).label + "s"}</option>)}
          </select>
        )}
        {counties.length > 1 && (
          <select className="select radar-pick" aria-label="County" value={county} onChange={e => setCounty(e.target.value)}>
            <option value="all">All counties</option>
            {counties.map((c: any) => <option key={c} value={c}>{countyLabel(c)}</option>)}
          </select>
        )}
      </div>

      <div className="card pad">
        {filtered.length === 0
          ? (live && territories !== null && !territorySet && view !== "mine"
            // no territory picked yet → the setup CTA, not a dead end
            ? <TerritoryPicker coverage={cov} territories={territories}
                onSave={async (entries: any) => { await saveTerritories(entries); await load(); }} />
            : <EmptyState icon="target"
                title={view === "mine" ? "No claims yet" : "No leads on the radar"}
                sub={view === "mine"
                  ? "Claim a lead from the feed — you get a 72-hour exclusive window to reach the homeowner first."
                  : (() => {
                      const mine = cov.filter((s: any) => (!territorySet || terrIds.has(s.id)) && (s.status === "live" || s.status === "stale"));
                      const last = mine.filter((s: any) => s.last_run_at).sort((a: any, b: any) => String(b.last_run_at).localeCompare(String(a.last_run_at)))[0];
                      // "Next automated run: Monday" is only true when something
                      // actually runs — an empty radar with nothing live gets
                      // the honest reason and a pointer, not false comfort
                      return last
                        ? `Last run ${fmtShort(last.last_run_at)} found ${last.last_run_rows} lead${last.last_run_rows === 1 ? "" : "s"} in your territory. Next automated run: Monday morning.`
                        : "No source has completed an ingest yet — the radar can only show what a source has pulled. The Sources panel below shows each feed and what it's waiting on.";
                    })()} />)
          : <div style={{ display: "grid", gap: 9 }}>
              {filtered.map((l: any) => (
                <LeadRow key={l.id} lead={l} lens={lens} mine={isMine(l)} claim={claimByPermit.get(l.id)}
                  onOpen={(x: any) => { setErr(""); setSel(x); }} />
              ))}
            </div>}
      </div>
      </>)}

      {status !== "loading" && <SourcesPanel rows={srcRows} note={srcNote} />}

      <p className="faint" style={{ fontSize: 11, lineHeight: 1.6, margin: "14px 4px 0", maxWidth: 760 }}>{copy.footer}</p>

      {sel && <LeadDetail lead={sel} claim={selClaim} quota={quota} premium={premiumIds.has(sel.source_id)} store={store} go={go} lens={lens} busy={busy}
        onClaim={doClaim} onRelease={doRelease} onOutcome={doOutcome}
        onDraft={() => { setDraft(sel); }} onClose={() => { setSel(null); setErr(""); }} />}
      {draft && <DraftQuoteModal lead={draft} claim={claimByPermit.get(draft.id)} store={store} go={go} lens={lens} onClose={() => setDraft(null)} />}
    </div>
  );
}

Object.assign(window, { LeadRadarPage });

export { LeadRadarPage };
